import * as React from 'react';
import type {ReactNode} from 'react';
import {useIsMobile} from '../../hooks';
import {isMobileModalModeAvailable} from '../../lib/runtimeSettings';
import type {SelectOption} from '../../lib/selector-option';
import {useSelectIds} from './useSelectIds';
import {useSelectMenuState} from './useSelectMenuState';
import type {
  SelectChangeMeta,
  SelectFilterOption,
  SelectIsOptionDisabled,
  SelectIsOptionSelected,
  SelectValue,
} from './types';
import {
  defaultFilterOption,
  defaultIsOptionSelected,
  getFirstEnabledOptionIndex,
  getNextEnabledOptionIndex,
  isOptionEnabled,
} from './utils';

type UseSelectStateParams<T, V extends SelectValue, L extends ReactNode> = {
  isMulti: boolean;
  options: SelectOption<T, V, L>[];
  selectedOptions: SelectOption<T, V, L>[];
  onSelectionChange: (next: SelectOption<T, V, L>[], meta: SelectChangeMeta) => void;
  filterOption?: SelectFilterOption<T, V, L>;
  isOptionDisabled?: SelectIsOptionDisabled<T, V, L>;
  isOptionSelected?: SelectIsOptionSelected<T, V, L>;
  isCreatable?: boolean;
  onCreateOption?: (input: string) => SelectOption<T, V, L>;
  isValidNewOption?: (
    input: string,
    selectedOptions: SelectOption<T, V, L>[],
    options: SelectOption<T, V, L>[],
  ) => boolean;
  getValueLabel?: (option: SelectOption<T, V, L>) => string;
  menuIsOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  name?: string;
  error?: string;
  hideErrorMessage?: boolean;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  closeMenuOnSelect?: boolean;
  openMenuOnFocus?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  isSearchInDropdown?: boolean;
  searchable?: boolean;
};

function resolveValueLabel<T, V extends SelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L> | undefined,
  getValueLabel?: (option: SelectOption<T, V, L>) => string,
) {
  if (!option) return '';
  return getValueLabel?.(option) ?? String(option.label);
}

export function useSelectState<T, V extends SelectValue, L extends ReactNode>(
  params: UseSelectStateParams<T, V, L>,
) {
  const {
    isMulti,
    options,
    selectedOptions,
    onSelectionChange,
    filterOption = defaultFilterOption,
    isOptionDisabled,
    isOptionSelected = defaultIsOptionSelected,
    isCreatable = false,
    onCreateOption,
    isValidNewOption,
    getValueLabel,
    menuIsOpen,
    onMenuOpenChange,
    name,
    error,
    hideErrorMessage,
    disabled,
    loading,
    readOnly,
    closeMenuOnSelect,
    openMenuOnFocus = false,
    onKeyDown,
    onFocus,
    onBlur,
    inputValue: controlledInputValue,
    onInputChange,
    isSearchInDropdown,
    searchable = true,
  } = params;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const isMobile = useIsMobile() && isMobileModalModeAvailable();

  const [isFocused, setIsFocused] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const wasOpenRef = React.useRef(false);

  const singleSelected = isMulti ? undefined : selectedOptions[0];
  const valueLabel = React.useMemo(
    () => resolveValueLabel(singleSelected, getValueLabel),
    [singleSelected, getValueLabel],
  );

  const isSearchOnlyInput = searchable && (isMulti || Boolean(isSearchInDropdown));
  const isInputControlled = controlledInputValue !== undefined;
  const [internalInputValue, setInternalInputValue] = React.useState(
    searchable && !isSearchOnlyInput ? valueLabel : '',
  );
  const inputValue = isInputControlled
    ? (controlledInputValue ?? '')
    : internalInputValue;
  const setInputValue = React.useCallback(
    (next: string, notify = false) => {
      if (!isInputControlled) setInternalInputValue(next);
      if (notify) onInputChange?.(next);
    },
    [isInputControlled, onInputChange],
  );
  const resetInputTarget = isSearchOnlyInput ? '' : valueLabel;
  const resetInputValue = React.useCallback(
    (notify = false) => {
      setInputValue(resetInputTarget, notify);
    },
    [resetInputTarget, setInputValue],
  );

  const hasValue = selectedOptions.length > 0;
  const isBlocked = Boolean(disabled) || Boolean(loading) || Boolean(readOnly);
  const shouldForceClose = Boolean(disabled) || Boolean(readOnly);
  const hasInvalidState = Boolean(error);
  const errorMessage = typeof error === 'string' ? error : undefined;

  const {containerRef, isOpen, closeMenu, setIsOpen} = useSelectMenuState({
    isBlocked,
    shouldForceClose,
    isMobile,
    onOutsideClick: () => setIsFocused(false),
    controlledIsOpen: menuIsOpen,
    onMenuOpenChange,
  });

  const ids = useSelectIds({name, hasValue, error, hideErrorMessage});

  React.useEffect(() => {
    if (!searchable) return;
    if (isSearchOnlyInput) return;
    if (!isFocused) setInputValue(valueLabel);
  }, [valueLabel, isFocused, isSearchOnlyInput, searchable, setInputValue]);

  React.useEffect(() => {
    if (!searchable) return;
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (isOpen || !wasOpen) {
      return;
    }

    resetInputValue(inputValue !== resetInputTarget);
    setHighlightedIndex(-1);
  }, [inputValue, isOpen, resetInputTarget, resetInputValue, searchable]);

  const trimmedInput = inputValue.trim();
  const isFiltering =
    searchable &&
    (isSearchOnlyInput
      ? trimmedInput.length > 0
      : trimmedInput.length > 0 &&
        trimmedInput.toLowerCase() !== valueLabel.toLowerCase());

  const filteredOptions = React.useMemo(() => {
    if (!isFiltering) return options;
    return options.filter(
      option => Boolean(option.onAction) || filterOption(option, trimmedInput),
    );
  }, [options, isFiltering, trimmedInput, filterOption]);

  const canCreateNewOption = React.useMemo(() => {
    if (!isCreatable || !trimmedInput) return false;
    if (isValidNewOption) {
      return isValidNewOption(trimmedInput, selectedOptions, options);
    }
    const lower = trimmedInput.toLowerCase();
    const existsInOptions = options.some(
      option => typeof option.label === 'string' && option.label.toLowerCase() === lower,
    );
    const existsInSelected = selectedOptions.some(
      option => typeof option.label === 'string' && option.label.toLowerCase() === lower,
    );
    return !existsInOptions && !existsInSelected;
  }, [isCreatable, trimmedInput, isValidNewOption, options, selectedOptions]);

  React.useEffect(() => {
    if (!isOpen || filteredOptions.length === 0) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(current => {
      if (current >= 0 && current < filteredOptions.length) return current;
      return getFirstEnabledOptionIndex(filteredOptions, isOptionDisabled);
    });
  }, [isOpen, filteredOptions, isOptionDisabled]);

  React.useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    optionRefs.current[highlightedIndex]?.scrollIntoView?.({block: 'nearest'});
  }, [highlightedIndex, isOpen]);

  const inMenuSearchVisible = searchable && (isMobile || Boolean(isSearchInDropdown));
  React.useEffect(() => {
    if (!isOpen || !inMenuSearchVisible) return;
    const frame = window.requestAnimationFrame(() =>
      mobileSearchInputRef.current?.focus(),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, inMenuSearchVisible]);

  const isValueSelected = React.useCallback(
    (option: SelectOption<T, V, L>) => isOptionSelected(option, selectedOptions),
    [isOptionSelected, selectedOptions],
  );

  const selectOption = React.useCallback(
    (option: SelectOption<T, V, L>) => {
      if (!isOptionEnabled(option, isOptionDisabled)) return;
      if (option.onAction) {
        setIsOpen(false);
        setIsFocused(false);
        inputRef.current?.blur();
        option.onAction();
        return;
      }
      if (isMulti) {
        const exists = isValueSelected(option);
        const next = exists
          ? selectedOptions.filter(item => item.value !== option.value)
          : [...selectedOptions, option];
        onSelectionChange(next, {action: exists ? 'deselect' : 'select'});
        setInputValue('', true);
        if (closeMenuOnSelect) {
          setIsOpen(false);
        } else {
          inputRef.current?.focus();
        }
        return;
      }
      onSelectionChange([option], {action: 'select'});
      setInputValue(
        searchable && !isSearchInDropdown ? resolveValueLabel(option, getValueLabel) : '',
        true,
      );
      setIsOpen(false);
      setIsFocused(false);
      inputRef.current?.blur();
    },
    [
      isOptionDisabled,
      isMulti,
      onSelectionChange,
      setInputValue,
      searchable,
      isSearchInDropdown,
      getValueLabel,
      setIsOpen,
      isValueSelected,
      selectedOptions,
      closeMenuOnSelect,
    ],
  );

  const removeOption = React.useCallback(
    (option: SelectOption<T, V, L>) => {
      if (isBlocked) return;
      onSelectionChange(
        selectedOptions.filter(item => item.value !== option.value),
        {action: 'deselect'},
      );
      setInputValue('', true);
      inputRef.current?.focus();
    },
    [isBlocked, onSelectionChange, selectedOptions, setInputValue],
  );

  const clearSelection = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      if (isBlocked) return;
      onSelectionChange([], {action: 'clear'});
      setInputValue('', true);
      inputRef.current?.focus();
    },
    [isBlocked, onSelectionChange, setInputValue],
  );

  const createOption = React.useCallback(() => {
    if (!canCreateNewOption) return;
    const newOption =
      onCreateOption?.(trimmedInput) ??
      ({value: trimmedInput as V, label: trimmedInput as L} as SelectOption<T, V, L>);
    if (isMulti) {
      onSelectionChange([...selectedOptions, newOption], {action: 'create'});
      setInputValue('', true);
      inputRef.current?.focus();
      if (closeMenuOnSelect) setIsOpen(false);
      return;
    }
    onSelectionChange([newOption], {action: 'create'});
    setInputValue(
      searchable && !isSearchInDropdown
        ? resolveValueLabel(newOption, getValueLabel)
        : '',
      true,
    );
    setIsOpen(false);
    setIsFocused(false);
    inputRef.current?.blur();
  }, [
    canCreateNewOption,
    onCreateOption,
    trimmedInput,
    isMulti,
    onSelectionChange,
    selectedOptions,
    closeMenuOnSelect,
    setIsOpen,
    getValueLabel,
    isSearchInDropdown,
    searchable,
    setInputValue,
  ]);

  const handleInputChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    event => {
      if (!searchable) return;
      const nextValue = event.target.value;
      setInputValue(nextValue, true);
      if (!isOpen) setIsOpen(true);
    },
    [isOpen, searchable, setIsOpen, setInputValue],
  );

  const handleInputFocus = React.useCallback<React.FocusEventHandler<HTMLInputElement>>(
    event => {
      if (isBlocked) return;
      onFocus?.(event);
      setIsFocused(true);
      if (openMenuOnFocus) setIsOpen(true);
      if (searchable && !isMulti) {
        requestAnimationFrame(() => inputRef.current?.select());
      }
    },
    [isBlocked, onFocus, openMenuOnFocus, setIsOpen, searchable, isMulti],
  );

  const handleTriggerFocus = React.useCallback<
    React.FocusEventHandler<HTMLElement>
  >(() => {
    if (isBlocked) return;
    setIsFocused(true);
    if (openMenuOnFocus) setIsOpen(true);
  }, [isBlocked, openMenuOnFocus, setIsOpen]);

  const handleContainerClick = React.useCallback(() => {
    if (isBlocked) return;
    inputRef.current?.focus();
    setIsOpen(true);
  }, [isBlocked, setIsOpen]);

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (containerRef.current?.contains(event.relatedTarget as Node)) return;
      const relatedElement =
        event.relatedTarget instanceof Element ? event.relatedTarget : null;
      if (relatedElement?.closest('[data-select-menu]')) return;
      setIsFocused(false);
      if (isMobile) {
        onBlur?.(event);
        return;
      }
      setIsOpen(false);
      onBlur?.(event);
    },
    [containerRef, isMobile, setIsOpen, onBlur],
  );

  const handleInputKeyDown = React.useCallback<
    React.KeyboardEventHandler<HTMLInputElement>
  >(
    event => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;

      if (
        isMulti &&
        event.key === 'Backspace' &&
        !inputValue &&
        selectedOptions.length > 0
      ) {
        event.preventDefault();
        onSelectionChange(selectedOptions.slice(0, -1), {action: 'deselect'});
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        const next = getNextEnabledOptionIndex(
          filteredOptions,
          highlightedIndex + 1,
          1,
          isOptionDisabled,
        );
        if (next >= 0) setHighlightedIndex(next);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        const next = getNextEnabledOptionIndex(
          filteredOptions,
          highlightedIndex - 1,
          -1,
          isOptionDisabled,
        );
        if (next >= 0) setHighlightedIndex(next);
        return;
      }

      if (event.key === 'Enter') {
        if (!isOpen) return;
        event.preventDefault();
        const option = filteredOptions[highlightedIndex];
        if (option && isOptionEnabled(option, isOptionDisabled)) {
          selectOption(option);
        } else if (canCreateNewOption) {
          createOption();
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        if (searchable) resetInputValue(true);
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [
      onKeyDown,
      isMulti,
      inputValue,
      selectedOptions,
      onSelectionChange,
      isOpen,
      setIsOpen,
      filteredOptions,
      highlightedIndex,
      isOptionDisabled,
      selectOption,
      canCreateNewOption,
      createOption,
      searchable,
      resetInputValue,
    ],
  );

  const isEmpty = !hasValue && !inputValue;

  React.useDebugValue({isOpen, isMulti, highlightedIndex, selectedOptions});

  return {
    ids,
    containerRef,
    inputRef,
    mobileSearchInputRef,
    listRef,
    registerOptionRef: (index: number, node: HTMLButtonElement | null) => {
      optionRefs.current[index] = node;
    },
    isOpen,
    closeMenu,
    inputValue,
    trimmedInput,
    valueLabel,
    filteredOptions,
    highlightedIndex,
    setHighlightedIndex,
    isFocused,
    setIsFocused,
    isBlocked,
    hasValue,
    isEmpty,
    hasInvalidState,
    errorMessage,
    isMobile,
    inMenuSearchVisible,
    canCreateNewOption,
    selectOption,
    removeOption,
    clearSelection,
    createOption,
    handleInputChange,
    handleInputFocus,
    handleTriggerFocus,
    handleInputKeyDown,
    handleContainerClick,
    handleBlur,
  };
}

export type {UseSelectStateParams};
